import { ScriptType } from "@/enums/campaigns";
import type { ScriptRevisionParam } from "@/types/scriptVersioning";
import { scriptTypeToWireSegment } from "@/utils/scriptVersioning/scriptTypeSegments";

export type ScriptDirection = "inbound" | "outbound";

export type ScriptEditorPathArgs = {
  direction: ScriptDirection;
  scriptId: string;
  /** When versioning is on, revision is required (except callers that pass a default). */
  revision?: ScriptRevisionParam | number;
  scriptType?: ScriptType;
  /** When true, use `/script/[id]/[revision]` URLs. When false, legacy `/scripts/[id]`. */
  versioning: boolean;
  search?: string;
};

/**
 * Versioned draft/publish editors are only wired for phone scripts today.
 * Typed outbound editors (text/email/blaster) still save via v1 and must stay on legacy routes.
 */
export const supportsVersionedScriptEditor = (scriptType?: ScriptType): boolean =>
  scriptType === undefined || scriptType === ScriptType.Phone;

const NEW_SCRIPT_ID = "new";

export const isNewScriptId = (scriptId: string): boolean => scriptId === NEW_SCRIPT_ID;

export const normalizeRevisionParam = (
  revision: ScriptRevisionParam | number | undefined
): ScriptRevisionParam => {
  if (revision === undefined) return "latest";
  if (typeof revision === "number") {
    if (!Number.isInteger(revision) || revision < 1) {
      throw new Error(`Invalid script revision: ${revision}`);
    }
    return String(revision) as ScriptRevisionParam;
  }
  if (revision === "latest" || revision === "draft") return revision;
  if (/^\d+$/.test(revision) && Number(revision) >= 1) return revision;
  throw new Error(`Invalid script revision param: ${revision}`);
};

export const parseRevisionParam = (
  raw: string
): { kind: "alias"; value: "latest" | "draft" } | { kind: "number"; value: number } | null => {
  if (raw === "latest" || raw === "draft") {
    return { kind: "alias", value: raw };
  }
  if (/^\d+$/.test(raw)) {
    const value = Number(raw);
    if (value >= 1) return { kind: "number", value };
  }
  return null;
};

/** Outbound editor URLs omit the type segment for phone; other types use the shared wire map. */
const outboundTypeSegment = (scriptType: ScriptType | undefined): string | null => {
  if (scriptType === undefined) return null;
  const segment = scriptTypeToWireSegment(scriptType);
  return segment === "phone" ? null : segment;
};

/**
 * Builds the in-app editor path for a script.
 * Legacy: `/inbound/scripts/[id]` or `/outbound/scripts/[type?]/[id]`
 * Versioning: `/inbound/script/[id]/[revision]` (create: `/…/script/new/draft`)
 */
export const getScriptEditorPath = ({
  direction,
  scriptId,
  revision,
  scriptType,
  versioning,
  search,
}: ScriptEditorPathArgs): string => {
  const query = search ? (search.startsWith("?") ? search : `?${search}`) : "";

  if (!versioning) {
    if (direction === "inbound") {
      return `/inbound/scripts/${scriptId}${query}`;
    }
    const typeSeg = outboundTypeSegment(scriptType);
    if (typeSeg) {
      return `/outbound/scripts/${typeSeg}/${scriptId}${query}`;
    }
    return `/outbound/scripts/${scriptId}${query}`;
  }

  const rev =
    isNewScriptId(scriptId) && (revision === undefined || revision === "latest")
      ? "draft"
      : normalizeRevisionParam(revision ?? "latest");

  if (direction === "inbound") {
    return `/inbound/script/${scriptId}/${rev}${query}`;
  }

  const typeSeg = outboundTypeSegment(scriptType);
  if (typeSeg) {
    return `/outbound/script/${typeSeg}/${scriptId}/${rev}${query}`;
  }
  return `/outbound/script/${scriptId}/${rev}${query}`;
};

/**
 * Maps a versioned editor URL back to the legacy path (drops revision).
 */
export const getLegacyScriptEditorPath = (args: Omit<ScriptEditorPathArgs, "versioning" | "revision">): string =>
  getScriptEditorPath({ ...args, versioning: false });

/**
 * When the org is not on scripts API v2, versioned routes should redirect here.
 */
export const getVersioningFlagOffRedirectPath = (args: {
  direction: ScriptDirection;
  scriptId: string;
  scriptType?: ScriptType;
  search?: string;
}): string => getLegacyScriptEditorPath(args);
